import React, { useState } from "react";
import FormGroup from "../components/FormGroup";
import InputText, { InputNumberFormat } from "../components/InputText";
import { useStepper } from "../Stepper";
import Divider from "components/Divider";
import styled from "styled-components";
import Checkbox from "components/Checkbox";
import ScoreWheel from "components/ScoreWheel";
import InputSelect from "components/InputSelect";
import Heading from "components/Heading";
import config from "config";
import Button from "components/Button";
import creditCardType from "credit-card-type";
import { colors } from "styles";
import { useConfirm } from "components/Confirm";
import FormError from "components/FormError";
import apiRequest from "utils/apiRequest";
import { useFullScreenSpinner } from "components/FullScreenSpinner";
import * as Sentry from "@sentry/react";
import parseDataBeforeSending from "utils/parseDataBeforeSending";
import analytics from "utils/analytics";
import { redirect } from "utils/windowRelocation";
import BubbleChat from "components/BubbleChat";

const CURRENT_YEAR = new Date().getFullYear() % 100;
const MONTHS = new Array(12).fill(1).map((v, i) => v + i);
const YEARS = new Array(11).fill(1).map((_, i) => i + CURRENT_YEAR);
const InputWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  max-width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  margin: auto;
  align-items: center;
  justify-content: center;
  padding: 5px 0;
`;

const PlanWrapper = styled.div`
  margin-left: 20px;
`;

const ALLOWED_CARD_BRANDS = ["visa", "mastercard"];

const Payment = () => {
  const { open } = useConfirm();
  const { isLoading, showSpinner, hideSpinner } = useFullScreenSpinner();
  const { getDefault, back } = useStepper();
  const [cardNumber, setCardNumber] = useState(
    getDefault("payment")?.cardNumber || ""
  );
  const [expMonth, setExpMonth] = useState(
    getDefault("payment")?.expMonth || ""
  );
  const [expYear, setExpYear] = useState(getDefault("payment")?.expYear || "");
  const [cvv, setCVV] = useState(getDefault("payment")?.cvv || "");
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [paymentDisclaimer, setPaymentDisclaimer] = useState(false);
  const [cardType, setCardType] = useState(
    creditCardType(cardNumber).length === 1
      ? creditCardType(cardNumber)[0]?.type
      : ""
  );
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState(null);
  const [addressData, setAddressData] = useState({
    address: getDefault("address")?.address || "",
    addressSecondary: getDefault("address")?.addressSecondary || "",
    city: getDefault("address")?.city || "",
    state: getDefault("address")?.state || "",
    stateLong: getDefault("address")?.stateLong || "",
    zipCode: getDefault("address")?.zipCode || "",
  });

  const canContinue =
    termsAndConditions &&
    paymentDisclaimer &&
    !!cardNumber &&
    !!expMonth &&
    expYear &&
    !!cvv &&
    cvv.length === 3 &&
    cardNumber.length === 16 &&
    ALLOWED_CARD_BRANDS.includes(cardType) &&
    !!addressData.address &&
    !!addressData.addressSecondary &&
    !!addressData.city &&
    !!addressData.state &&
    !!addressData.zipCode;

  const plan = config.plans.find(({ id }) => id === getDefault("plan")?.id);
  const price = plan.methods.find(
    ({ id }) => id === getDefault("plan")?.methodId
  );

  return (
    <>
      {/* <Heading
        title={`Review de información, pago y terminos y condiciones`}
        body={``}
      /> */}

      {/* <Divider /> */}

      <Wrapper>
        <ScoreWheel
          $size="medium"
          score={getDefault("recommendations")?.simulatedScore}
          disabled
        />
        <PlanWrapper>
          <h1 className="text-xl">${price.amount / 100}</h1>
          <div>{price.frecuency}</div>
          <div className="text-sm">Puede cancelar en cualquier momento</div>
        </PlanWrapper>
      </Wrapper>

      {/* <Divider /> */}

      <form
        onSubmit={async (e) => {
          e.preventDefault();
        }}
        style={{
          paddingTop: 20,
        }}
      >
        <FormGroup>
          <div
            style={{
              position: "relative",
            }}
          >
            {cardType && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: -17,
                  opacity: 0.5,
                }}
              >
                {creditCardType.getTypeInfo(cardType).niceType}
              </div>
            )}
            <InputNumberFormat
              name="cardNumber"
              placeholder="Numero de la tarjeta *"
              format="####-####-####-####"
              mask="_"
              value={cardNumber}
              onValueChange={({ value }) => {
                setCardType(
                  value ? creditCardType(value.replace("-", ""))[0]?.type : ""
                );
                setCardNumber(value);
              }}
              required
              pattern="[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}"
              subtext="Campo obligatorio"
            />

            {cardType && !ALLOWED_CARD_BRANDS.includes(cardType) && (
              <FormError>
                Disculpe, pero solo se aceptan tarjetas Visa o MasterCard en
                este momento.
              </FormError>
            )}
          </div>
          <InputWrapper>
            <InputSelect
              name="expMonth"
              required
              placeholder={"Mes de Expiracion *"}
              onChange={(e) => setExpMonth(e.target.value)}
              defaultValue={expMonth}
              subtext="Campo obligatorio"
            >
              {MONTHS.map((value, i) => (
                <option key={`expiration-month-${i}`}>{value}</option>
              ))}
            </InputSelect>
            <InputSelect
              name="expYear"
              required
              placeholder={"Año de Expiracion *"}
              onChange={(e) => setExpYear(e.target.value)}
              defaultValue={expYear}
              subtext="Campo obligatorio"
            >
              {YEARS.map((value, i) => (
                <option key={`expiration-year-${i}`}>{value}</option>
              ))}
            </InputSelect>
            <div>
              <InputText
                name="cvv"
                placeholder="CVV *"
                required
                value={cvv}
                onChange={(e) => setCVV(e.target.value)}
                minLength="3"
                maxLength="3"
                subtext="Campo obligatorio"
              />
            </div>
          </InputWrapper>

          {/* ADDRESS */}
          <InputText
            name="address"
            placeholder="Direccion *"
            required
            value={addressData.address}
            onChange={(e) =>
              setAddressData((d) => ({ ...d, address: e.target.value }))
            }
            subtext="Campo obligatorio"
          />
          <InputText
            name="addressSecondary"
            placeholder="Apartamento, Suite, etc *"
            value={addressData.addressSecondary}
            onChange={(e) =>
              setAddressData((d) => ({
                ...d,
                addressSecondary: e.target.value,
              }))
            }
            subtext="Campo obligatorio"
          />
          <InputText
            name="city"
            placeholder="Ciudad / Pueblo *"
            required
            value={addressData.city}
            onChange={(e) =>
              setAddressData((d) => ({
                ...d,
                city: e.target.value,
              }))
            }
            subtext="Campo obligatorio"
          />
          <InputWrapper>
            <InputText
              name="state"
              placeholder="Estado / País *"
              required
              value={addressData.state}
              onChange={(e) =>
                setAddressData((d) => ({
                  ...d,
                  state: e.target.value,
                }))
              }
              subtext="Campo obligatorio"
            />
            <InputText
              name="zipCode"
              placeholder="Zip code / Código Postal *"
              required
              value={addressData.zipCode}
              onChange={(e) =>
                setAddressData((d) => ({
                  ...d,
                  zipCode: e.target.value,
                }))
              }
              subtext="Campo obligatorio"
            />
          </InputWrapper>

          <div>
            <Checkbox
              id="payment_disclaimer"
              checked={paymentDisclaimer}
              onChange={() => {
                setPaymentDisclaimer((v) => !v);
              }}
            >
              {price?.id === "biweekly" && (
                <>
                  Importante, usted escogió el programa quincenal. Sus pagos
                  serán programado automáticamente para quince (15) días
                  posteriores a su primer pago.
                </>
              )}
              {price?.id === "monthly" && (
                <>
                  Importante, usted escogió el programa mensual. Sus pagos serán
                  programados automáticamente taranta (30) días posteriores a su
                  primer pago.
                </>
              )}
            </Checkbox>
            <Checkbox
              id="terms_and_conditions"
              checked={termsAndConditions}
              onChange={() => {
                setTermsAndConditions((v) => !v);
              }}
            >
              <a
                href="/terminos-y-condiciones"
                target="_blank"
                onClick={(e) => {
                  e.preventDefault();

                  setShowTerms((v) => !v);
                }}
              >
                Acepto los Terminos y Condiciones
              </a>
            </Checkbox>

            {showTerms && (
              <Terms>
                <p>
                  DECLARACIÓN DE LOS DERECHOS DEL CONSUMIDOR AL AMPARO DE LAS
                  DISPOSICIONES ESTATALES Y FEDERALES (esp.)
                </p>
                <p>
                  Usted tiene derecho a disputar toda información incorrecta que
                  aparezca en un informe sobre su historial crediticio
                  comunicándose directamente con la entidad u organización de
                  informe de crédito que haya emitido el informe adverso. A
                  pesar de este derecho, ni usted ni ninguna Agencia
                  Re-establecedora de Crédito tendrá derecho a que se excluya
                  información de su expediente crediticio si la misma resulta
                  ser información correcta, certera, vigente y verificable. La
                  organización de informes de crédito deberá remover toda
                  información negativa que, a pesar de ser certera, tenga más de
                  siete (7) años en el mismo. Información relacionada a casos de
                  quiebras podrá permanecer en el expediente o historial
                  crediticio del consumidor por un término máximo de diez (10)
                  años. Usted tiene derecho a obtener copias de informes de
                  crédito de las organizaciones de informes de crédito, por lo
                  cual, podrían cobrarle un cargo por servicio. Sin embargo, no
                  habrá cargos por servicios en caso de que, en un término de
                  sesenta (60) días previos, a usted le hayan denegado el
                  crédito, empleo, seguro o arrendamiento de vivienda por motivo
                  de la información que surja de un informe crediticio negativo.
                  Las organizaciones de informes de crédito deberán proveerle
                  asistencia social o si tiene fundamento para creer que existe
                  información errónea o inexacta relacionada a fraude. Se
                  requiere que las organizaciones de informes de crédito tomen
                  las medidas y procedimientos razonables a fin de que sus
                  informes sean certeros. Sin embargo, se entiende que puede
                  haber errores. Todo consumidor podrá notificar, por cuenta
                  propia y por escrito, a una organización de informes de
                  crédito su posición de impugnar la certeza de información de
                  su expediente crediticio. La organización de informes de
                  crédito deberá re-investigar, modificar o remover toda
                  información pertinente y copias de documentos relacionados a
                  procedimientos de corrección deberán ser ofrecidos a la
                  organización de informes de crédito. Disponiéndose, que en
                  caso de que la organización no resuelva la disputa a la
                  satisfacción del consumidor, este podrá enviar un escrito
                  exponiendo los fundamentos para alegar que la información es
                  incompleta o dudosa; disponiéndose, además, que dicho escrito
                  se incluirá en el expediente crediticio del consumidor y un
                  resumen del mismo formara parte de todo informe de crédito que
                  dicha organización emita prospectivamente.
                </p>
              </Terms>
            )}
          </div>

          {error && (
            <div
              style={{
                color: colors.white,
                backgroundColor: colors.red,
                padding: 20,
                textAlign: "center",
                fontSize: "1.2em",
              }}
            >
              {error}
            </div>
          )}
        </FormGroup>

        <Button
          $fluid
          disabled={!canContinue}
          type="button"
          onClick={() => {
            if (!isLoading) {
              open(
                <>
                  Antes de realizar su pago debe asegurarse de tener disponible
                  una copia de su identification con foto, copia de su Seguro
                  Social, y 2 pruebas de direccion vigentes.
                  <br />
                  <br />
                  Usted tiene <strong>7 días</strong> a partir de su pago para
                  completar la documentación, de lo contrario su caso pudiera
                  verse cancelado.
                </>,
                async () => {
                  showSpinner(
                    "Procesando... Espere por favor (no cierre la aplication)."
                  );

                  try {
                    const { firstName, lastName, email, phoneNumber } =
                      getDefault("personalInfo");
                    const { zipCode } = getDefault("address");
                    const { address, addressSecondary, city, state } =
                      addressData;
                    const data = {
                      firstName: firstName || lastName,
                      lastName: lastName || firstName,
                      email,
                      phoneNumber,
                      addressZip: zipCode,
                      cardNumber,
                      cardExpMonth: expMonth,
                      cardExpYear: "20" + expYear,
                      cardCvv: cvv,
                      plan: [plan.id, price.id].filter(Boolean).join("-"),
                      fixReasons: getDefault("reason"),
                      currentCreditScore: getDefault("currentScore"),
                      problemsAffectingScore:
                        getDefault("currentProblems")?.join(","),
                      selectedRecommendations:
                        getDefault("recommendations").selectedOptions?.join(
                          ","
                        ),
                    };
                    const parsedData = parseDataBeforeSending(data);

                    try {
                      await apiRequest("POST", "/client", parsedData);

                      analytics.sendEvent("purchase", {
                        currency: "USD",
                        transaction_id: Date.now(),
                        value: price?.amount ? price.amount / 100 : 0,
                        items: [
                          {
                            item_id: plan.id,
                            item_name: plan.name,
                            price: price?.amount ? price.amount / 100 : 0,
                          },
                        ],
                      });

                      redirect("/proximos-pasos");
                    } catch (error) {
                      console.error("ERROR", error.message);

                      Sentry.captureMessage(`Cannot create Client`, {
                        extra: {
                          error,
                          errorMsg: error?.message || "N/A",
                          data,
                          parsedData,
                        },
                        level: Sentry.Severity.Error,
                      });

                      setError(
                        error?.type === "email-exists" ? (
                          `El email "${email}" que ha ingresado ya esta en uso.`
                        ) : (
                          <>
                            Ha ocurrido un error. Por favor intentelo nuevamente
                            o contacte soporte tecnico
                            {error?.message && (
                              <>
                                <br />
                                <br />
                                <strong>Razon:</strong> {error.message}
                              </>
                            )}
                          </>
                        )
                      );

                      hideSpinner();
                    }
                  } catch (error) {
                    console.error(error);
                    Sentry.captureMessage(`Payment submit handler`, {
                      extra: {
                        error,
                        errorMsg: error?.message || "N/A",
                      },
                      level: Sentry.Severity.Error,
                    });

                    setError(
                      "Ha ocurrido un error. Por favor intentelo nuevamente o contacte soporte tecnico"
                    );
                  }
                  // END
                }
              );
            }
          }}
        >
          Pagar ${price.amount / 100} y Continuar
        </Button>

        <Button
          $fluid
          $type="tertiary"
          type="button"
          onClick={() => {
            open("Seguro desea cancelar?", () => {
              back();
            });
          }}
        >
          Cancelar
        </Button>
      </form>

      <BubbleChat />
    </>
  );
};

export default Payment;

const Terms = styled.div`
  max-height: 200px;
  overflow: auto;
  background: white;
  border: 1px ${colors.gray} solid;
  padding: 5px 15px;
  color: #666;

  p {
    font-size: 0.9em;
  }
`;
